import { useCallback, useEffect, useMemo, useState } from 'react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadLinksPreset } from '@tsparticles/preset-links';
import type { Container, Engine, ISourceOptions } from '@tsparticles/engine';

let particlesEngineInit: Promise<void> | null = null;

function ensureParticlesEngine(): Promise<void> {
  particlesEngineInit ??= initParticlesEngine(async (engine: Engine) => {
    await loadLinksPreset(engine);
  });
  return particlesEngineInit;
}

export function ParticlesBackground() {
  const [init, setInit] = useState(false);

  useEffect(() => {
    let mounted = true;
    ensureParticlesEngine().then(() => {
      if (mounted) setInit(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const particlesLoaded = useCallback(async (_container?: Container): Promise<void> => {
    // Particles loaded
  }, []);

  const options = useMemo<ISourceOptions>(
    () => ({
      fullScreen: { enable: false },
      preset: 'links',
      background: {
        color: 'transparent',
      },
      particles: {
        color: {
          value: ['#8B5CF6', '#A78BFA', '#7C3AED', '#94a3b8'],
        },
        links: {
          color: '#8B5CF6',
          distance: 150,
          enable: true,
          opacity: 0.2,
          width: 1,
        },
        move: {
          enable: true,
          speed: 1.2,
          direction: 'none',
          random: false,
          straight: false,
          outModes: {
            default: 'out',
          },
        },
        number: {
          density: {
            enable: true,
          },
          value: 200,
        },
        opacity: {
          value: { min: 0.3, max: 0.7 },
        },
        shape: {
          type: 'circle',
        },
        size: {
          value: { min: 1, max: 3 },
        },
      },
      interactivity: {
        detectsOn: 'window',
        events: {
          onHover: {
            enable: true,
            mode: 'repulse',
          },
        },
        modes: {
          repulse: {
            distance: 180,
            duration: 0.4,
            factor: 100,
            speed: 1,
            maxSpeed: 50,
          },
        },
      },
      detectRetina: true,
    }),
    [],
  );

  if (!init) return null;

  return (
    <Particles
      id="tsparticles"
      particlesLoaded={particlesLoaded}
      className="absolute inset-0 w-full h-full"
      options={options}
    />
  );
}
